(() => {
  const handleize = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const normalizeDimension = (value) => {
    const match = String(value || '').match(/\d+(?:\.\d+)?/);
    return match ? String(Number(match[0])) : handleize(value);
  };

  const initPricing = (root) => {
    if (!root || root.dataset.inkReady === 'true') return;

    const variantJson = root.querySelector('[data-ink-pricing-variants]');
    const optionNameJson = root.querySelector('[data-ink-pricing-option-names]');
    if (!variantJson || !optionNameJson) return;

    let variants;
    let optionNames;
    try {
      variants = JSON.parse(variantJson.textContent);
      optionNames = JSON.parse(optionNameJson.textContent);
    } catch (error) {
      console.error('Unable to read Deal pricing variants.', error);
      return;
    }

    if (!Array.isArray(variants) || !variants.length || !Array.isArray(optionNames)) return;

    const optionHandles = optionNames.map(handleize);
    const optionIndexes = Object.fromEntries(optionHandles.map((name, index) => [name, index]));
    if (optionIndexes.size === undefined || optionIndexes.finish === undefined || optionIndexes.quantity === undefined) {
      console.warn('Deal products require Size, Finish, and Quantity Shopify options.');
      return;
    }

    root.dataset.inkReady = 'true';
    const sectionId = root.dataset.sectionId;
    const currency = root.dataset.currency || 'USD';
    const locale = root.dataset.locale || document.documentElement.lang || 'en-US';
    const unitLabel = root.dataset.unitLabel || 'stickers';
    const popularQuantity = String(root.dataset.popularQuantity || '100');
    const optionValues = (variant) => (variant.options || [variant.option1, variant.option2, variant.option3]).map(String);
    const valueFor = (variant, name) => optionValues(variant)[optionIndexes[name]];
    const money = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const formatPrice = (cents) => money.format(Number(cents || 0) / 100);
    const formatDelta = (cents) => {
      if (!cents) return 'Same price';
      return `${cents > 0 ? '+' : '−'}${formatPrice(Math.abs(cents))}`;
    };
    const matchesDefaults = (variant) => (
      normalizeDimension(valueFor(variant, 'size')) === normalizeDimension(root.dataset.defaultSize)
      && handleize(valueFor(variant, 'finish')) === handleize(root.dataset.defaultFinish)
      && String(valueFor(variant, 'quantity')) === String(root.dataset.defaultQuantity)
    );
    const urlVariantId = Number(new URLSearchParams(window.location.search).get('variant'));
    const selectedVariantId = Number(root.dataset.selectedVariantId);
    const initialVariant = variants.find((variant) => variant.id === urlVariantId)
      || variants.find((variant) => variant.id === selectedVariantId && matchesDefaults(variant))
      || variants.find((variant) => variant.available && matchesDefaults(variant))
      || variants.find((variant) => variant.available)
      || variants[0];
    const state = Object.fromEntries(optionHandles.map((name, index) => [name, optionValues(initialVariant)[index]]));
    const findVariant = (nextState = state) => variants.find((variant) => optionHandles.every(
      (name) => String(valueFor(variant, name)) === String(nextState[name])
    ));
    const availableVariants = () => variants.filter((variant) => variant.available !== false);
    const ensureValidState = (changedName) => {
      const exact = findVariant();
      if (exact?.available !== false) return exact;
      const candidate = availableVariants().find((variant) => valueFor(variant, changedName) === state[changedName])
        || availableVariants()[0]
        || variants[0];
      optionHandles.forEach((name) => { state[name] = valueFor(candidate, name); });
      return candidate;
    };

    const quantityPicker = root.querySelector('[data-ink-quantity-picker]');
    const quantityTrigger = root.querySelector('[data-ink-quantity-trigger]');
    const quantityCurrent = root.querySelector('[data-ink-quantity-current]');
    const quantityMenu = root.querySelector('[data-ink-quantity-menu]');
    const quantityOptions = root.querySelector('[data-ink-quantity-options]');
    const guideLink = root.querySelector('[data-ink-guide-open]');
    const sizeButtons = [...root.querySelectorAll('[data-ink-options="size"] [data-value]')];
    const finishButtons = [...root.querySelectorAll('[data-ink-options="finish"] [data-value]')];
    const allQuantities = [...new Set(variants.map((variant) => valueFor(variant, 'quantity')))]
      .sort((a, b) => Number(a) - Number(b));

    const placeAfterHighlights = () => {
      const productInfo = root.closest('.product-info');
      const buyBlock = productInfo?.querySelector('.x-block-buy-buttons');
      const highlights = productInfo?.querySelector('.ip-hero-benefits');
      if (!buyBlock) return false;
      if (highlights) {
        if (highlights.nextElementSibling !== root) highlights.after(root);
        return true;
      }
      return false;
    };

    if (!placeAfterHighlights()) {
      const productInfo = root.closest('.product-info');
      if (productInfo) {
        const placementObserver = new MutationObserver(() => {
          if (placeAfterHighlights()) placementObserver.disconnect();
        });
        placementObserver.observe(productInfo, { childList: true, subtree: true });
        window.setTimeout(() => {
          placementObserver.disconnect();
          const buyBlock = productInfo.querySelector('.x-block-buy-buttons');
          if (buyBlock && !productInfo.querySelector('.ip-hero-benefits')) buyBlock.before(root);
        }, 3000);
      }
    }

    const nativePicker = () => document.querySelector('[data-ink-native-variant-picker]');
    const persistTransferVariantId = (variant) => {
      if (root.dataset.productHandle !== 'custom-transfer-stickers' || !variant?.id) return;
      const productForm = document.getElementById(`product-form-${sectionId}`);
      const nativeInput = document.getElementById(`update-variant-${sectionId}`);
      if (!productForm) return;
      let input = document.getElementById(`ink-transfer-variant-${sectionId}`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.id = `ink-transfer-variant-${sectionId}`;
        input.name = 'id';
        productForm.append(input);
        input.addEventListener('change', () => {
          const current = findVariant();
          if (current) persistTransferVariantId(current);
        });
      }
      if (nativeInput && nativeInput !== input) nativeInput.removeAttribute('name');
      const variantId = String(variant.id);
      if (input.value !== variantId) input.value = variantId;
      if (input.getAttribute('value') !== variantId) input.setAttribute('value', variantId);
    };
    const setNativeOption = (optionName, value) => {
      const group = nativePicker()?.querySelector(`[data-option-name="${optionName}"]`);
      if (!group) return false;
      const control = [...group.querySelectorAll('input, select')]
        .find((item) => String(item.value) === String(value));
      if (!control) return false;
      if (control.tagName === 'SELECT') {
        if (String(control.value) === String(value)) return true;
        control.value = value;
      } else {
        if (control.checked) return true;
        control.checked = true;
      }
      control.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    };
    const syncNativeVariant = (variant) => {
      optionHandles.forEach((name) => setNativeOption(name, state[name]));
      if (root.dataset.productHandle === 'custom-transfer-stickers') {
        persistTransferVariantId(variant);
        return;
      }
      const formIdInput = document.getElementById(`update-variant-${sectionId}`);
      if (formIdInput && String(formIdInput.value) !== String(variant.id)) {
        formIdInput.value = variant.id;
        formIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    const quantityVariant = (quantity) => findVariant({ ...state, quantity: String(quantity) });
    const renderQuantityOptions = () => {
      if (!quantityOptions) return;
      quantityOptions.innerHTML = allQuantities.map((quantity) => {
        const variant = quantityVariant(quantity);
        if (!variant || variant.available === false) return '';
        const unit = Number(variant.price) / 100 / Number(quantity);
        const selected = String(quantity) === String(state.quantity);
        const popular = String(quantity) === popularQuantity;
        return `
          <button
            class="ink-pricing__quantity-option${selected ? ' is-current' : ''}${popular ? ' is-popular' : ''}"
            type="button"
            data-value="${quantity}"
            role="option"
            aria-selected="${selected}"
          >
            <span class="ink-pricing__quantity-value">
              <span>${quantity}</span>
              ${popular ? '<span class="ink-pricing__popular-badge">Most Popular</span>' : ''}
            </span>
            <span>${formatPrice(variant.price)}</span>
            <span>${money.format(unit)} each</span>
          </button>`;
      }).join('');
    };

    const setQuantityMenu = (open) => {
      if (!quantityMenu) return;
      quantityMenu.hidden = !open;
      quantityTrigger?.setAttribute('aria-expanded', String(open));
      guideLink?.setAttribute('aria-expanded', String(open));
      quantityPicker?.classList.toggle('is-open', open);
    };

    const publishSelection = (variant) => {
      const quantity = Math.max(1, Number(state.quantity || 1));
      const unitPrice = Math.round(Number(variant.price || 0) / quantity) / 100;
      const detail = {
        sectionId,
        variant,
        options: { ...state },
        quantity,
        unitLabel,
        total: Number(variant.price || 0) / 100,
        totalFormatted: formatPrice(variant.price),
        unitFormatted: money.format(unitPrice)
      };

      document.querySelectorAll('[data-ink-deal-total]').forEach((element) => {
        element.textContent = detail.totalFormatted;
      });
      document.querySelectorAll('[data-ink-deal-unit]').forEach((element) => {
        element.textContent = detail.unitFormatted;
      });

      document.dispatchEvent(new CustomEvent('ink:deal-pricing:updated', { detail }));
    };

    const render = () => {
      const current = ensureValidState('quantity');
      if (!current) return;
      persistTransferVariantId(current);
      if (quantityCurrent) quantityCurrent.textContent = state.quantity;
      const minimum = root.querySelector('[data-ink-minimum]');
      if (minimum) minimum.textContent = allQuantities[0];

      sizeButtons.forEach((button) => {
        const selected = button.dataset.value === state.size;
        const candidate = findVariant({ ...state, size: button.dataset.value });
        button.disabled = !candidate || candidate.available === false;
        button.setAttribute('aria-pressed', String(selected));
        const delta = button.querySelector('[data-ink-delta]');
        if (delta) delta.textContent = selected || !candidate ? '' : formatDelta(Number(candidate.price) - Number(current.price));
      });
      finishButtons.forEach((button) => {
        const selected = button.dataset.value === state.finish;
        const candidate = findVariant({ ...state, finish: button.dataset.value });
        button.disabled = !candidate || candidate.available === false;
        button.setAttribute('aria-pressed', String(selected));
        const delta = button.querySelector('[data-ink-delta]');
        if (delta) delta.textContent = selected || !candidate ? '' : formatDelta(Number(candidate.price) - Number(current.price));
      });

      const total = root.querySelector('[data-ink-total]');
      const summaryQuantity = root.querySelector('[data-ink-summary-quantity]');
      const unitPrice = root.querySelector('[data-ink-unit-price]');
      if (total) total.textContent = formatPrice(current.price);
      if (summaryQuantity) summaryQuantity.textContent = state.quantity;
      if (unitPrice) unitPrice.textContent = money.format(Number(current.price) / 100 / Number(state.quantity));
      renderQuantityOptions();
      publishSelection(current);
    };

    const selectOption = (name, value) => {
      if (state[name] === String(value)) return;
      state[name] = String(value);
      const variant = ensureValidState(name);
      render();
      syncNativeVariant(variant);
    };

    quantityTrigger?.addEventListener('click', () => setQuantityMenu(quantityMenu?.hidden !== false));
    guideLink?.addEventListener('click', () => setQuantityMenu(quantityMenu?.hidden !== false));
    quantityOptions?.addEventListener('click', (event) => {
      const option = event.target.closest('[data-value]');
      if (!option) return;
      selectOption('quantity', option.dataset.value);
      setQuantityMenu(false);
      quantityTrigger?.focus();
    });
    sizeButtons.forEach((button) => button.addEventListener('click', () => selectOption('size', button.dataset.value)));
    finishButtons.forEach((button) => button.addEventListener('click', () => selectOption('finish', button.dataset.value)));

    document.addEventListener('click', (event) => {
      if (quantityMenu?.hidden !== false) return;
      if (quantityPicker?.contains(event.target) || guideLink?.contains(event.target)) return;
      setQuantityMenu(false);
    });
    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || quantityMenu?.hidden !== false) return;
      setQuantityMenu(false);
      quantityTrigger?.focus();
    });
    document.addEventListener(`eurus:product-page-variant-select:updated:${sectionId}`, () => {
      window.setTimeout(() => {
        const selectedJson = nativePicker()?.querySelector('[data-selected-variant]');
        if (!selectedJson) return;
        try {
          const selected = JSON.parse(selectedJson.textContent);
          if (!selected) return;
          optionHandles.forEach((name) => { state[name] = valueFor(selected, name); });
          render();
          window.setTimeout(() => persistTransferVariantId(selected), 100);
        } catch (error) {
          console.error('Unable to sync Deal pricing selection.', error);
        }
      }, 0);
    });

    if (root.dataset.productHandle === 'custom-transfer-stickers') {
      const productForm = document.getElementById(`product-form-${sectionId}`);
      const persistCurrentVariant = () => {
        const current = findVariant();
        if (current) persistTransferVariantId(current);
      };
      productForm?.addEventListener('submit', persistCurrentVariant, true);
      productForm?.querySelector('[name="add"]')?.addEventListener('click', persistCurrentVariant, true);
      if (productForm) {
        const variantInputObserver = new MutationObserver(persistCurrentVariant);
        variantInputObserver.observe(productForm, {
          attributes: true,
          attributeFilter: ['value'],
          childList: true,
          subtree: true
        });
      }
      window.setTimeout(persistCurrentVariant, 250);
    }

    syncNativeVariant(initialVariant);
    render();
  };

  const initAllPricing = (scope = document) => {
    scope.querySelectorAll('[data-ink-variant-pricing]').forEach(initPricing);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllPricing());
  } else {
    initAllPricing();
  }
  document.addEventListener('shopify:section:load', (event) => initAllPricing(event.target));
})();
